import React, { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx-js-style";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useMessages } from "context/messages";
import { getToken } from "context/auth";

import Widget from "components/dashboard/widget";
import WidgetLoadingSkeleton from "components/dashboard/widgetLoadingSkeleton";
import ButtonText from "components/common/buttonText";
import InputSearch from "components/common/inputSearch";
import Paging from "components/common/paging";
import Modal from "components/common/modal";

import { getObjectIdsForArea, bulkUpdateObjectIds, previewObjectIds } from "api/objectIds";

const ObjectIdsPage = () => {
  const { t } = useTranslation();
  const { showToast } = useMessages();
  const { parkingAreaExtId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [spots, setSpots] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const fileInputRef = useRef(null);
  const PAGE_SIZE = 10;

  // Anton-review-backfill 2026-04-30 — Patriks UX-krav: per-rad-preview innan submit.
  const [previewItems, setPreviewItems] = useState(null); // raw items att postas
  const [previewResult, setPreviewResult] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewFilter, setPreviewFilter] = useState("all"); // "all" | "changes" | "noChange" | "issues"

  const load = async () => {
    try {
      setIsLoading(true);
      const token = await getToken();
      const data = await getObjectIdsForArea(token, parkingAreaExtId);
      setSpots(data || []);
    } catch (e) {
      showToast(t("objectIds.loadFailed"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [parkingAreaExtId]);

  const handleDownload = () => {
    // A3: Excel-export sorteras numeriskt på Platsnummer (1, 2, 3 … 20),
    // matchar systemets visning i UI. Tomma platsnummer hamnar sist.
    const sortedSpots = [...spots].sort((a, b) => {
      const aHas = !!(a.spotIdentifier || "").trim();
      const bHas = !!(b.spotIdentifier || "").trim();
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      return (a.spotIdentifier || "").localeCompare(b.spotIdentifier || "", "sv", {
        numeric: true,
        sensitivity: "base",
      });
    });
    const rows = sortedSpots.map((s) => ({
      "Plats-ID": s.parkingSpotExtId,
      "Platsnummer": s.spotIdentifier || "",
      "Objekt-id": s.objectID || "",
    }));
    const headers = ["Plats-ID", "Platsnummer", "Objekt-id"];
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });

    // 🔒-ikon i header-text för låst kolumn — fungerar i alla program oavsett färg-stöd.
    if (ws["A1"]) ws["A1"].v = "🔒 Plats-ID";

    // Stark grå (#D1D5DB) på låsta celler så markeringen syns även i LibreOffice.
    const LOCKED_DATA_FILL = { fgColor: { rgb: "D1D5DB" } };
    const LOCKED_HEADER_FILL = { fgColor: { rgb: "9CA3AF" } };
    const EDITABLE_HEADER_FILL = { fgColor: { rgb: "F3F4F6" } };
    const HEADER_FONT = { bold: true };

    const lockedColumns = new Set([0]); // Plats-ID
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" };
        const isHeader = R === 0;
        const isLocked = lockedColumns.has(C);
        if (isHeader) {
          ws[addr].s = { font: HEADER_FONT, fill: isLocked ? LOCKED_HEADER_FILL : EDITABLE_HEADER_FILL };
        } else if (isLocked) {
          ws[addr].s = { fill: LOCKED_DATA_FILL };
        }
      }
    }
    ws["!cols"] = [{ wch: 16 }, { wch: 16 }, { wch: 18 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Parkeringsplatser");
    XLSX.writeFile(wb, `objektid_${parkingAreaExtId}.xlsx`);
  };

  const closePreview = () => {
    setShowPreviewModal(false);
    setPreviewItems(null);
    setPreviewResult(null);
    setPreviewFilter("all");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Action-grupper för filter-pills.
  const CHANGE_ACTIONS = ["setObjectID", "changeObjectID", "clearObjectID", "renameSpot", "clearSpotIdentifier", "setAndRename"];
  const ISSUE_ACTIONS = ["skipUnknown", "skipDupObjectId", "skipDupSpotId", "skipUnnumberedRename", "error"];

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      let wb;
      try {
        wb = XLSX.read(data);
      } catch (err) {
        showToast(t("objectIds.xlsxParseError"), "error");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      const items = [];
      for (const row of rows) {
        // Acceptera både "🔒 Plats-ID" (Excel-headern visar 🔒-ikon) och "Plats-ID"
        const parkingSpotExtId = (row["Plats-ID"] ?? row["🔒 Plats-ID"] ?? "").toString().trim();
        if (!parkingSpotExtId) continue;
        const objectId = (row["Objekt-id"] ?? "").toString().trim() || null;
        const spotIdentifier = (row["Platsnummer"] ?? "").toString().trim() || null;
        items.push({ parkingSpotExtId, objectID: objectId, spotIdentifier });
      }

      if (items.length === 0) {
        showToast(t("objectIds.noRowsFound"), "error");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      // Hämta backend-preview
      setIsPreviewLoading(true);
      try {
        const token = await getToken();
        const previewRes = await previewObjectIds(token, parkingAreaExtId, items);
        setPreviewItems(items);
        setPreviewResult(previewRes);
        setShowPreviewModal(true);
      } catch (err) {
        const msg = err?.json ? (await err.json()).message : t("objectIds.previewFailed");
        showToast(msg, "error");
        if (fileInputRef.current) fileInputRef.current.value = "";
      } finally {
        setIsPreviewLoading(false);
      }
    } catch (e) {
      showToast(t("objectIds.uploadFailed"), "error");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleConfirmUpload = async () => {
    if (!previewItems) return;
    try {
      setIsUploading(true);
      const token = await getToken();
      const result = await bulkUpdateObjectIds(token, parkingAreaExtId, previewItems);
      showToast(`${t("objectIds.updated")}: ${result.updatedCount}`, "success");
      closePreview();
      await load();
    } catch (e) {
      const msg = e?.json ? (await e.json()).message : t("objectIds.uploadFailed");
      showToast(msg, "error");
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) return <WidgetLoadingSkeleton />;

  // Action-badge-helper för preview-tabellen
  const renderActionBadge = (action) => {
    const map = {
      setObjectID: { label: t("objectIds.actionSet"), cls: "bg-green-100 text-green-700" },
      changeObjectID: { label: t("objectIds.actionChange"), cls: "bg-blue-100 text-blue-700" },
      clearObjectID: { label: t("objectIds.actionClear"), cls: "bg-orange-100 text-orange-700" },
      renameSpot: { label: t("objectIds.actionRename"), cls: "bg-purple-100 text-purple-700" },
      clearSpotIdentifier: { label: t("objectIds.actionClearSpot"), cls: "bg-orange-100 text-orange-700" },
      setAndRename: { label: t("objectIds.actionSetAndRename"), cls: "bg-emerald-100 text-emerald-700" },
      noChange: { label: t("objectIds.actionNoChange"), cls: "bg-gray-100 text-gray-600" },
      skipUnknown: { label: t("objectIds.actionSkipUnknown"), cls: "bg-red-100 text-red-700" },
      skipDupObjectId: { label: t("objectIds.actionSkipDupObj"), cls: "bg-orange-100 text-orange-700" },
      skipDupSpotId: { label: t("objectIds.actionSkipDupSpot"), cls: "bg-orange-100 text-orange-700" },
      skipUnnumberedRename: { label: t("objectIds.actionSkipUnnumbered"), cls: "bg-gray-200 text-gray-700" },
      error: { label: t("objectIds.actionError"), cls: "bg-red-100 text-red-700" },
    };
    const b = map[action] || { label: action, cls: "bg-gray-100 text-gray-600" };
    return (
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${b.cls}`} data-testid={`objectids-preview-action`}>
        {b.label}
      </span>
    );
  };

  return (
    <Widget title={t("objectIds.title")}>
      <p className="mb-4">{t("objectIds.description")}</p>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <InputSearch
          placeHolder={t("generic.search")}
          value={searchText}
          onChange={(value) => { setSearchText(value); setPage(0); }}
        />
        <div className="flex gap-2 justify-end">
          <ButtonText
            isSecondary={true}
            onClick={handleDownload}
            className="h-9 pl-4 pr-4 text-sm">
            {t("objectIds.download")}
          </ButtonText>
          <ButtonText
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isPreviewLoading}
            className="h-9 pl-4 pr-4 text-sm">
            {isPreviewLoading ? t("objectIds.previewLoading") : (isUploading ? t("objectIds.uploading") : t("objectIds.upload"))}
          </ButtonText>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileSelected}
          />
        </div>
      </div>

      {(() => {
        const q = (searchText || "").trim().toLowerCase();
        const filtered = [...spots]
          .filter((s) => {
            if (!q) return true;
            return (
              (s.parkingSpotExtId || "").toLowerCase().includes(q) ||
              (s.spotIdentifier || "").toLowerCase().includes(q) ||
              (s.objectID || "").toLowerCase().includes(q)
            );
          })
          .sort((a, b) => {
            const aHas = !!(a.spotIdentifier || "").trim();
            const bHas = !!(b.spotIdentifier || "").trim();
            if (aHas && !bHas) return -1;
            if (!aHas && bHas) return 1;
            return (a.spotIdentifier || "").localeCompare(b.spotIdentifier || "", "sv", {
              numeric: true,
              sensitivity: "base",
            });
          });

        const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const safePage = Math.min(page, totalPages - 1);
        const slice = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

        return (
          <>
            <table className="w-full">
              <thead>
                <tr>
                  <th className="pl-6 text-left whitespace-nowrap">{t("objectIds.id")}</th>
                  <th className="pl-6 text-left whitespace-nowrap">{t("objectIds.spotIdentifier")}</th>
                  <th className="pl-6 text-left whitespace-nowrap">{t("objectIds.objectId")}</th>
                </tr>
              </thead>
              <tbody>
                {slice.map((s) => (
                  <tr key={s.parkingSpotExtId} className="h-12">
                    <td className="pl-6">{s.parkingSpotExtId}</td>
                    <td className="pl-6">{s.spotIdentifier || t("objectIds.unnumbered")}</td>
                    <td className="pl-6">{s.objectID || "-"}</td>
                  </tr>
                ))}
                {slice.length === 0 && (
                  <tr><td colSpan={3} className="pl-6 py-3 text-gray-500">{t("objectIds.noResults")}</td></tr>
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <Paging
                totalNumberOfPages={totalPages}
                currentPage={safePage}
                setCurrentPage={setPage}
              />
            )}
          </>
        );
      })()}

      <Modal
        display={showPreviewModal}
        setDisplay={setShowPreviewModal}
        onClose={closePreview}
        title={t("objectIds.previewTitle")}
        innerClassName="!w-[1100px] !max-w-[95vw] bg-white rounded-lg p-6"
      >
        {previewResult && (() => {
          const s = previewResult.summary;
          const totalChange = s.setObjectID + s.changeObjectID + s.clearObjectID + s.renameSpot + s.clearSpotIdentifier + s.setAndRename;
          const totalIssues = s.skipUnknown + s.skipDuplicateObjectId + s.skipDuplicateSpotId + s.skipUnnumberedRename + s.errorRows;

          const rows = previewResult.rows.filter((row) => {
            if (previewFilter === "all") return true;
            if (previewFilter === "changes") return CHANGE_ACTIONS.includes(row.action);
            if (previewFilter === "noChange") return row.action === "noChange";
            if (previewFilter === "issues") return ISSUE_ACTIONS.includes(row.action);
            return true;
          });

          const FilterPill = ({ value, label, count, color }) => {
            const active = previewFilter === value;
            const base = "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors";
            const inactive = "bg-white text-gray-600 border-airpark-gray-400 hover:bg-airpark-gray-300";
            const activeCls = {
              gray: "bg-gray-700 text-white border-gray-700",
              green: "bg-green-600 text-white border-green-600",
              blue: "bg-blue-600 text-white border-blue-600",
              red: "bg-red-600 text-white border-red-600",
            }[color] || "bg-gray-700 text-white border-gray-700";
            return (
              <button
                type="button"
                onClick={() => setPreviewFilter(value)}
                className={`${base} ${active ? activeCls : inactive}`}
                data-testid={`objectids-filter-${value}`}
              >
                {label} <span className="ml-1 opacity-80">({count})</span>
              </button>
            );
          };

          const KpiCard = ({ label, value, color }) => {
            const palette = {
              gray: "bg-airpark-gray-300 text-gray-700",
              green: "bg-green-50 text-green-700 border-green-200",
              blue: "bg-blue-50 text-blue-700 border-blue-200",
              red: "bg-red-50 text-red-700 border-red-200",
            }[color] || "bg-airpark-gray-300 text-gray-700";
            return (
              <div className={`flex-1 min-w-[140px] rounded-lg border border-transparent ${palette} px-4 py-3`}>
                <div className="text-2xl font-semibold leading-none">{value}</div>
                <div className="text-xs mt-1 opacity-80">{label}</div>
              </div>
            );
          };

          const renderDiff = (before, after, action) => {
            const b = before || "";
            const a = after ?? before ?? "";
            const isChange = before !== after && (action !== "noChange");
            if (!isChange) {
              return <span className="text-gray-700">{b || <span className="text-gray-400">—</span>}</span>;
            }
            return (
              <span className="inline-flex items-center gap-1.5">
                <span className="line-through text-gray-400">{b || "—"}</span>
                <span className="text-gray-400">→</span>
                <span className="font-medium text-gray-800">{a || "—"}</span>
              </span>
            );
          };

          return (
            <div className="flex flex-col gap-4" data-testid="objectids-preview-block">
              <p data-testid="objectids-preview-summary" className="text-sm text-gray-600">
                {t("objectIds.previewSummary", { total: s.totalRows, changes: totalChange })}
              </p>

              <div className="flex gap-3 flex-wrap">
                <KpiCard label={t("objectIds.kpiTotal")} value={s.totalRows} color="gray" />
                <KpiCard label={t("objectIds.kpiChanges")} value={totalChange} color="green" />
                <KpiCard label={t("objectIds.kpiNoChange")} value={s.noChange} color="blue" />
                <KpiCard label={t("objectIds.kpiIssues")} value={totalIssues} color={totalIssues > 0 ? "red" : "gray"} />
              </div>

              <div className="flex gap-2 flex-wrap items-center">
                <span className="text-xs text-gray-500 mr-1">{t("objectIds.filterLabel")}</span>
                <FilterPill value="all" label={t("objectIds.filterAll")} count={s.totalRows} color="gray" />
                <FilterPill value="changes" label={t("objectIds.filterChanges")} count={totalChange} color="green" />
                <FilterPill value="noChange" label={t("objectIds.filterNoChange")} count={s.noChange} color="blue" />
                {totalIssues > 0 && (
                  <FilterPill value="issues" label={t("objectIds.filterIssues")} count={totalIssues} color="red" />
                )}
              </div>

              <div className="overflow-x-auto border border-airpark-gray-400 rounded-xl max-h-[420px]">
                <table className="w-full text-sm" data-testid="objectids-preview-table">
                  <thead className="sticky top-0 bg-airpark-gray-300 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium w-12 text-gray-600">#</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{t("objectIds.id")}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{t("objectIds.spotIdentifier")}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{t("objectIds.objectId")}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{t("objectIds.previewColAction")}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{t("objectIds.previewColExplanation")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.rowIndex}
                        className="border-t border-airpark-gray-400 align-top hover:bg-airpark-gray-300/50"
                        data-testid={`objectids-preview-row-${row.rowIndex}`}
                      >
                        <td className="px-3 py-2 text-gray-500">{row.rowIndex}</td>
                        <td className="px-3 py-2 text-xs text-gray-700">{row.parkingSpotExtId || "—"}</td>
                        <td className="px-3 py-2">{renderDiff(row.currentSpotIdentifier, row.newSpotIdentifier, row.action)}</td>
                        <td className="px-3 py-2">{renderDiff(row.currentObjectID, row.newObjectID, row.action)}</td>
                        <td className="px-3 py-2">{renderActionBadge(row.action)}</td>
                        <td className="px-3 py-2 text-gray-600" data-testid={`objectids-preview-message-${row.rowIndex}`}>{row.message}</td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-gray-500" data-testid="objectids-preview-empty">
                          {t("objectIds.filterNoResults")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-2 justify-end mt-2">
                <ButtonText isSecondary onClick={closePreview} disabled={isUploading}>
                  {t("generic.cancel")}
                </ButtonText>
                <ButtonText onClick={handleConfirmUpload} disabled={isUploading || totalChange === 0}>
                  {isUploading ? t("objectIds.uploading") : t("objectIds.confirmUpload")}
                </ButtonText>
              </div>
            </div>
          );
        })()}
      </Modal>
    </Widget>
  );
};

export default ObjectIdsPage;
